import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dbLabel } from "../../i18n/dbLabels";
import { filterAllblogs, removeBlogBookMarkApi, blogBookMarkApi } from "../../apis/WebServices";
import { UserContext } from "../../contexts/UserContext";
import { message } from "antd";
import Loader from "../loader/Loader";
import { useNavigate } from "react-router-dom";
import { app } from "@microsoft/teams-js";

// Import shared skeleton system
import { SkeletonBlogCard } from "../skeletons";

// Smart Navigation Helper Functions
const getCurrentEntityId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('entityId') || 'blog-library';
};

const getPreviousEntityId = () => {
    return localStorage.getItem('previousEntityId') || null;
};

const setPreviousEntityId = (entityId) => {
    localStorage.setItem('previousEntityId', entityId);
};

const navigateToTeamsTab = async (targetEntityId) => {
    try {
        console.log(`🎯 Navigating to Teams tab: ${targetEntityId}`);
        
        if (window.microsoftTeams && window.microsoftTeams.pages && window.microsoftTeams.pages.isSupported()) {
            const teamsAppId = import.meta.env.VITE_TEAMS_APP_ID;
            
            const navPromise = window.microsoftTeams.pages.navigateToApp({
                appId: teamsAppId,
                pageId: targetEntityId
            });
            
            await navPromise;
            console.log(`✅ Successfully navigated to ${targetEntityId} tab`);
        } else {
            console.error('❌ Teams SDK pages navigation not available');
        }
    } catch (error) {
        console.error('❌ Teams navigation failed:', error);
    }
};

// Import category icons
import trainingIcon from "../../assets/training-blue.png";
import nutritionIcon from "../../assets/nutrition-blue.png";
import lifestyleIcon from "../../assets/lifestyle.png";

const BlogLibrary = () => {
    const { userToken } = useContext(UserContext);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [blogs, setBlogs] = useState([]);
    const [allBlogs, setAllBlogs] = useState([]); // Store all blogs for filtering
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [bookmarkLoadingIds, setBookmarkLoadingIds] = useState([]);
    const [BookMarkedTrueIds, setAllBookMarkedTrueIds] = useState([]);
    const [bookMarkedStatus, setBookMarkedStatus] = useState(false);
    const [hasCheckedDeepLink, setHasCheckedDeepLink] = useState(false);
    
    // Category filtering state
    const [selectedCategories, setSelectedCategories] = useState({
        'TRAINING': true,
        'NUTRITION': true, 
        'LIFESTYLE': true
    });

    // Check for deep link from notification - IMMEDIATE redirect
    useEffect(() => {
        const checkForNotificationDeepLink = async () => {
            if (hasCheckedDeepLink) return;
            
            try {
                // Get Teams context to check for subEntityId
                if (window.microsoftTeams) {
                    const context = await window.microsoftTeams.app.getContext();
                    console.log('📱 Teams Context:', context);
                    
                    // Check if we have a subEntityId from notification
                    if (context?.page?.subPageId) {
                        const subPageId = context.page.subPageId;
                        console.log('🔔 Notification SubPageId:', subPageId);
                        
                        // Extract blogId from subPageId (format: "blogId=xxxx")
                        if (subPageId.startsWith('blogId=')) {
                            const blogId = subPageId.replace('blogId=', '');
                            console.log('📚 Direct navigation to blog from notification:', blogId);
                            
                            // IMMEDIATE redirect to blog detail - don't wait for component to render
                            window.location.replace(`#/blog-detail?blogId=${blogId}&from=notification`);
                            return; // Stop execution
                        }
                    }
                    setHasCheckedDeepLink(true);
                } else {
                    setHasCheckedDeepLink(true);
                }
            } catch (error) {
                console.error('❌ Error checking for deep link:', error);
                setHasCheckedDeepLink(true);
            }
        };

        // Run immediately
        checkForNotificationDeepLink();
    }, []); // Remove dependencies to run only once immediately

    const fetchAllBlogs = async () => {
        try {
            setLoading(true);
            setFetchError(false);
            const response = await filterAllblogs({ 
                userId: userToken, 
                selectedCatgId: '', 
                keyIds: [], 
                searchValue: '', 
                pageNumber: 1, 
                pageSize: 1000 
            });

            if (response?.isSuccess) {
                // Flatten all blogs from different categories
                const allBlogs = [];
                if (response?.data?.categoryWise) {
                    Object.values(response.data.categoryWise).forEach(categoryBlogs => {
                        if (Array.isArray(categoryBlogs)) {
                            allBlogs.push(...categoryBlogs);
                        }
                    });
                }
                
                // Store all blogs for filtering
                setAllBlogs(allBlogs);
                
                // Apply initial filtering
                filterBlogsByCategory(allBlogs, selectedCategories);
                
                // Set bookmarked IDs
                setAllBookMarkedTrueIds(
                    allBlogs?.map((key) => key?.isBookMarked ? key?.id : null).filter(id => id !== null)
                );
            }
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setFetchError(true);
            message.error(t('blogLibrary.loadFailedRetry'));
        } finally {
            setLoading(false);
        }
    };
    
    // Function to filter blogs by selected categories
    const filterBlogsByCategory = (blogList, categories) => {
        const selectedCategoryNames = Object.keys(categories).filter(cat => categories[cat]);
        
        if (selectedCategoryNames.length === 0) {
            // If no categories selected, show no blogs
            setBlogs([]);
            return;
        }
        
        const filteredBlogs = blogList.filter(blog => {
            const blogCategory = blog?.category?.name?.toUpperCase() || '';
            return selectedCategoryNames.includes(blogCategory);
        });
        
        setBlogs(filteredBlogs);
    };
    
    // Handle category toggle
    const handleCategoryToggle = (category) => {
        const newSelectedCategories = {
            ...selectedCategories,
            [category]: !selectedCategories[category]
        };
        
        setSelectedCategories(newSelectedCategories);
        filterBlogsByCategory(allBlogs, newSelectedCategories);
    };

    const handleAddBookMark = async (id) => {
        try {
            if (!userToken) {
                message.error(t('common.userNotLoggedIn'));
                return;
            }
            setBookMarkedStatus(true);
            setBookmarkLoadingIds(prev => [...prev, id]);
            if (BookMarkedTrueIds.includes(id)) {
                const response = await removeBlogBookMarkApi(id, userToken);

                if (response.isSuccess) {
                    message.success(response?.message)
                    setAllBookMarkedTrueIds((prevTrueIds) => prevTrueIds.filter((itemId) => itemId !== id));
                }
            } else {
                const response = await blogBookMarkApi(id, userToken);

                if (response.isSuccess) {
                    message.success(response?.message)
                    setAllBookMarkedTrueIds((prevTrueIds) => [...prevTrueIds, id]);
                }
            }
        } catch (error) {
            if (error?.isSuccess === false) {
                message.error(error?.message)
            }
        } finally {
            setBookMarkedStatus(false)
            setBookmarkLoadingIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    const handleBlogClick = (blog) => {
        const blogId = blog?.id;

        if (!blogId) {
            console.error('Missing blog ID:', blogId);
            message.error(t('blogLibrary.openBlogFailed'));
            return;
        }

        console.log('🎯 BlogLibrary: Navigating to blog detail', { blogId });
        
        // Store current context for smart back navigation
        const currentEntityId = getCurrentEntityId();
        setPreviousEntityId(currentEntityId);
        
        // Navigate to blog detail with context tracking
        navigate(`/blog-detail?blogId=${blogId}&from=blog-library&entityId=${currentEntityId}`);
        
        console.log('🔄 Same-tab navigation: Blog Library -> Blog Detail');
    };

    useEffect(() => {
        if (userToken) {
            fetchAllBlogs();
        }
        
        // Track navigation context when component loads
        const urlParams = new URLSearchParams(window.location.search);
        const fromParam = urlParams.get('from');
        const currentEntityId = getCurrentEntityId();
        
        console.log('📍 BlogLibrary loaded with context:', { currentEntityId, fromParam });
        
        // If user came from a specific tab, store it as previous context
        if (fromParam) {
            setPreviousEntityId(fromParam);
            console.log('💾 Stored previous entity ID:', fromParam);
        } else {
            // If no fromParam, check if we're in Blog Library entity context
            // This means user came via Teams SDK navigation from another tab
            const currentPath = window.location.pathname;
            if (currentPath === '/blog-library') {
                // Likely came from Home tab via Teams SDK navigation
                setPreviousEntityId('index');
                console.log('💾 Detected Teams SDK navigation - storing Home as previous entity');
            }
        }
    }, [userToken]);

    // Don't render anything if we're still checking for deep link
    if (!hasCheckedDeepLink && window.microsoftTeams) {
        return <Loader />; // Show loader while checking
    }

    if (loading) {
        return (
            <div style={{ minHeight: "100vh" }}>
                <div className="container-fluid">
                    <div className="row">
                        <div className="whole-div">
                            <div className="row page-content">
                                <div className="col-12">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h2 className="text-white mb-0" style={{ fontWeight: 'bold' }}>{t('blogLibrary.title')}</h2>
                                        <div className="d-flex align-items-center gap-3">
                                            <a style={{ color: '#6c757d', cursor: 'default' }}>
                                                <i className="ri ri-arrow-left-s-line" style={{ fontWeight: 'bold' }}></i> {t('common.back')}
                                            </a>
                                            <div style={{
                                                width: '1px',
                                                height: '30px',
                                                backgroundColor: '#6c757d',
                                                margin: '0 8px'
                                            }}></div>
                                            <div className="d-flex gap-3">
                                                {[t('blogLibrary.categoryTraining'), t('blogLibrary.categoryNutrition'), t('blogLibrary.categoryLifestyle')].map((label) => (
                                                    <button
                                                        key={label}
                                                        disabled
                                                        className="btn btn-outline-secondary"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            borderColor: '#4a4e59',
                                                            color: '#6c757d',
                                                            fontWeight: '500',
                                                            fontSize: '14px',
                                                            padding: '8px 16px',
                                                            borderRadius: '20px',
                                                            minWidth: '110px',
                                                            cursor: 'not-allowed'
                                                        }}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                {/* Show 8 blog skeleton cards */}
                                {[...Array(8)].map((_, index) => (
                                    <SkeletonBlogCard key={index} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh" }}>
            <div className="container-fluid">
                <div className="row">
                    <div className="whole-div">
                        <div className="row page-content">
                            <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h2 className="text-white mb-0" style={{ fontWeight: 'bold' }}>{t('blogLibrary.title')}</h2>
                                    
                                    {/* Back Button + Category Filter Buttons */}
                                    <div className="d-flex align-items-center gap-3">
                                        {/* Back Button */}
                                        <a 
                                            onClick={() => {
                                                console.log('🔙 BACK BUTTON: Smart navigation from Blog Library');
                                                
                                                // Get current navigation context
                                                const urlParams = new URLSearchParams(window.location.search);
                                                const fromParam = urlParams.get('from');
                                                const currentEntityId = getCurrentEntityId();
                                                const previousEntityId = getPreviousEntityId();
                                                
                                                console.log('📍 Navigation context:', { currentEntityId, previousEntityId, fromParam });
                                                
                                                // Smart navigation logic
                                                if (previousEntityId && previousEntityId !== currentEntityId) {
                                                    // Cross-tab navigation - use Teams SDK
                                                    console.log('🔄 Cross-tab navigation detected - using Teams SDK');
                                                    navigateToTeamsTab(previousEntityId);
                                                } else {
                                                    // Same-tab navigation or first visit - navigate to Home tab
                                                    console.log('🏠 Navigating to Home tab via Teams SDK');
                                                    navigateToTeamsTab('index');
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                transition: 'color 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.color = '#16f69f';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.color = '#fff';
                                            }}
                                        >
                                            <i className="ri ri-arrow-left-s-line" style={{ fontWeight: 'bold' }}></i> {t('common.back')}
                                        </a>
                                        
                                        {/* Vertical Separator */}
                                        <div style={{
                                            width: '1px',
                                            height: '30px',
                                            backgroundColor: '#6c757d',
                                            margin: '0 8px'
                                        }}></div>
                                        
                                        {/* Category Filter Buttons */}
                                        <div className="d-flex gap-3">
                                            {[
                                                { key: 'TRAINING', icon: trainingIcon, label: t('blogLibrary.categoryTraining') },
                                                { key: 'NUTRITION', icon: nutritionIcon, label: t('blogLibrary.categoryNutrition') },
                                                { key: 'LIFESTYLE', icon: lifestyleIcon, label: t('blogLibrary.categoryLifestyle') }
                                            ].map(({ key, icon, label }) => (
                                                <button
                                                    key={key}
                                                    onClick={() => handleCategoryToggle(key)}
                                                    className={`btn d-flex align-items-center ${
                                                        selectedCategories[key]
                                                            ? 'btn-outline-light' 
                                                            : 'btn-outline-secondary'
                                                    }`}
                                                    style={{
                                                        backgroundColor: selectedCategories[key] ? '#16f69f' : 'transparent',
                                                        borderColor: selectedCategories[key] ? '#16f69f' : '#6c757d',
                                                        color: selectedCategories[key] ? '#000' : '#fff',
                                                        fontWeight: '500',
                                                        fontSize: '14px',
                                                        padding: '8px 16px',
                                                        borderRadius: '20px',
                                                        transition: 'all 0.2s ease',
                                                        minWidth: '110px'
                                                    }}
                                                >
                                                    <img 
                                                        src={icon} 
                                                        alt={label}
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            marginRight: '8px',
                                                            filter: selectedCategories[key] ? 'none' : 'brightness(0) saturate(100%) invert(47%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)'
                                                        }}
                                                    />
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {fetchError ? (
                            <div className="row">
                                <div className="col-12 text-center">
                                    <h4 className="text-danger">{t('blogLibrary.unableToLoad')}</h4>
                                    <p className="text-white-50">{t('common.technicalDifficulties')}</p>
                                    <button
                                        className="btn btn-primary mt-2"
                                        onClick={() => {
                                            setFetchError(false);
                                            fetchAllBlogs();
                                        }}
                                    >
                                        {t('common.tryAgain')}
                                    </button>
                                </div>
                            </div>
                        ) : blogs.length === 0 ? (
                            <div className="row">
                                <div className="col-12 text-center">
                                    <h4 className="text-white-50">
                                        {Object.values(selectedCategories).every(cat => !cat)
                                            ? t('blogLibrary.pickCategory')
                                            : t('blogLibrary.noBlogsInCategory')}
                                    </h4>
                                </div>
                            </div>
                        ) : (
                            <div className="row">
                                {blogs.map((blog, index) => (
                                    <div key={blog.id || index} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                        <div className="card explore-box card-animate" style={{ minHeight: '350px', display: 'flex', flexDirection: 'column' }}>
                                            <div className="explore-place-bid-img" onClick={() => handleBlogClick(blog)}>
                                                <img
                                                    src={blog?.thumbnail || nutritionIcon}
                                                    alt={blog?.title || blog?.blogName}
                                                    className="img-fluid card-img-top explore-img"
                                                    style={{ height: "200px", objectFit: "cover" }}
                                                />
                                                <div className="bg-overlay"></div>
                                            </div>
                                            <div className="card-body" style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h6 className="card-title mb-0 flex-grow-1" 
                                                        style={{ 
                                                            cursor: "pointer", 
                                                            fontSize: "14px",
                                                            lineHeight: "1.2",
                                                            height: "17px",
                                                            overflow: "hidden",
                                                            whiteSpace: "nowrap",
                                                            textOverflow: "ellipsis"
                                                        }} 
                                                        onClick={() => handleBlogClick(blog)}>
                                                        <a className="text-primary">
                                                            {blog?.title || blog?.blogName}
                                                        </a>
                                                    </h6>
                                                    <button
                                                        className="btn btn-sm p-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAddBookMark(blog.id);
                                                        }}
                                                        disabled={bookmarkLoadingIds.includes(blog.id)}
                                                        style={{ background: "none", border: "none", cursor: "pointer" }}
                                                    >
                                                        <i className={`mdi ${BookMarkedTrueIds.includes(blog.id) ? "mdi-bookmark" : "mdi-bookmark-outline"} fs-18`} 
                                                           style={{
                                                               color: BookMarkedTrueIds.includes(blog.id) ? '#16f69f' : '#6c757d',
                                                               fontSize: '18px'
                                                           }}></i>
                                                    </button>
                                                </div>
                                                <div>
                                                    <p className="text-muted mb-1">
                                                        <small>{dbLabel(blog?.category?.name || "LIFESTYLE", i18n.language)}</small>
                                                    </p>
                                                    <p className="text-muted mb-0" style={{
                                                        overflow: "hidden",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical"
                                                    }}>
                                                        <small>{blog?.description || "Understanding the profound effects..."}</small>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogLibrary;